import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Resolve any stored image reference to a safe, absolute HTTPS URL on the current backend.
// - Relative "/api/files/..." -> prefixed with BACKEND_URL (https).
// - Legacy absolute "...<host>/api/files/..." -> re-pointed to BACKEND_URL (fixes stale preview hosts / http).
// - External absolute (Unsplash, Google avatars) or other relative assets -> returned as-is.
export function fileUrl(src) {
  if (!src || typeof src !== "string") return src;
  const marker = "/api/files/";
  if (/^https?:\/\//i.test(src)) {
    const idx = src.indexOf(marker);
    return idx !== -1 ? `${BACKEND_URL}${src.slice(idx)}` : src;
  }
  if (src.startsWith(marker)) return `${BACKEND_URL}${src}`;
  return src;
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 60000,
});

export const SESSION_EXPIRED_EVENT = "vivo:session-expired";
const NO_REFRESH = /\/auth\/(refresh|login|logout|register|verify|google|session)/;
let refreshing = null;
export const refreshSession = () => {
  if (!refreshing) refreshing = api.post("/auth/refresh", null, { __skipRefresh: true }).finally(() => { refreshing = null; });
  return refreshing;
};

const busy = { all: 0, actions: 0, timer: null };
const MUTATIONS = ["post", "put", "patch", "delete"];
const PAYMENT_RE = /\/(reserve|checkout|oneclick|publication-pay)(\/|$)/;
const LABELS = [
  [/pagar|pago/, "Pagando…"], [/reservar/, "Reservando…"], [/guardar|actualizar|crear|agregar|copiar/, "Guardando…"],
  [/enviar|preguntar|invitar/, "Enviando…"], [/eliminar|borrar|quitar|anular/, "Eliminando…"],
  [/ingresar|iniciar|entrar|registr/, "Ingresando…"],
];
const lastClick = { el: null, t: 0, label: null };
if (typeof document !== "undefined") {
  document.addEventListener("click", (e) => {
    const el = e.target.closest?.("button, [role='button']");
    if (el) { lastClick.el = el; lastClick.t = Date.now(); lastClick.label = busyLabel(el); lastClick.w = el.offsetWidth; }
  }, true);
}
const busyLabel = (el) => {
  const txt = (el.textContent || el.getAttribute("aria-label") || "").toLowerCase();
  return (LABELS.find(([re]) => re.test(txt)) || [null, "Procesando…"])[1];
};
const markButton = (config) => {
  const el = lastClick.el;
  if (!el || !el.isConnected || Date.now() - lastClick.t > 500 || el.classList.contains("btn-busy")) return;
  if ((lastClick.w || el.offsetWidth) < 70) return;
  el.style.setProperty("min-width", `${Math.max(lastClick.w || 0, 130)}px`);
  el.style.setProperty("--btn-busy-color", getComputedStyle(el).color);
  el.setAttribute("data-busy-label", lastClick.label || busyLabel(el));
  el.classList.add("btn-busy");
  config.__btn = el;
  lastClick.el = null;
};
const syncBusy = () => {
  const root = document.documentElement;
  root.classList.toggle("app-busy-lock", busy.actions > 0);
  if (busy.all > 0) {
    if (!busy.timer && !root.classList.contains("app-busy"))
      busy.timer = setTimeout(() => { busy.timer = null; if (busy.all > 0) root.classList.add("app-busy"); }, 300);
  } else {
    clearTimeout(busy.timer); busy.timer = null;
    root.classList.remove("app-busy");
  }
};
const done = (config) => {
  if (!config?.__busy) return;
  config.__busy = false;
  busy.all -= 1;
  if (config.__action) busy.actions -= 1;
  if (config.__btn) { config.__btn.classList.remove("btn-busy"); config.__btn.removeAttribute("data-busy-label"); config.__btn.style.removeProperty("--btn-busy-color"); config.__btn.style.removeProperty("min-width"); config.__btn = null; }
  syncBusy();
};
api.interceptors.request.use((config) => {
  config.__busy = true;
  config.__action = MUTATIONS.includes((config.method || "get").toLowerCase());
  busy.all += 1;
  if (config.__action) { busy.actions += 1; markButton(config); }
  syncBusy();
  return config;
});
const waitOnline = () => new Promise((resolve) => window.addEventListener("online", resolve, { once: true }));
api.interceptors.response.use(
  (res) => { done(res.config); return res; },
  async (err) => {
    const config = err.config;
    done(config);
    if (err.response?.status === 401 && config && !config.__skipRefresh && !config.__retried && !NO_REFRESH.test(config.url || "")) {
      config.__retried = true;
      try {
        await refreshSession();
        return api.request(config);
      } catch {
        if (!/\/auth\/me/.test(config.url || "")) window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
        return Promise.reject(err);
      }
    }
    if (err.code === "ECONNABORTED" && !err.response) {
      err.response = { status: 0, data: { detail: "La plataforma está tardando más de lo normal. Revisa tu conexión e intenta nuevamente." } };
      return Promise.reject(err);
    }
    if (!err.response && config && !config.offlineFallback && typeof navigator !== "undefined" && !navigator.onLine) {
      if (config.__action && PAYMENT_RE.test(config.url || "")) {
        err.response = { status: 0, data: { detail: "Sin conexión. Revisa Mis Tickets antes de volver a pagar." } };
        return Promise.reject(err);
      }
      if ((config.__retries || 0) < 3) {
        config.__retries = (config.__retries || 0) + 1;
        await waitOnline();
        return api.request(config);
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Intenta nuevamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
